import classNames from 'classnames'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { commonUtils } from '@/helpers/commonUtils'
import styles from './ProductSwitchHoverPanel.module.less'

export type ProductSwitchHoverPanelItem = {
  description: string
  isComingSoon?: boolean
  key: string
  onClick?: () => void
  title: string
}

export type ProductSwitchHoverPanelColumn = {
  assetText?: string
  bannerTitle: string
  items: ProductSwitchHoverPanelItem[]
  key: string
  tone: 'blue' | 'purple'
}

type Props = {
  className?: string
  columns: ProductSwitchHoverPanelColumn[]
}

function ProductSwitchHoverPanel({ className, columns }: Props) {
  const { t } = useTranslation()
  const [activeColumnKey, setActiveColumnKey] = useState(columns[0]?.key || '')

  const activeColumn = useMemo(() => {
    return columns.find((column) => column.key === activeColumnKey) || columns[0]
  }, [activeColumnKey, columns])

  const getIconSrc = (item: ProductSwitchHoverPanelItem) => {
    const isQuick = item.key.includes('quick')
    const isBrain = item.key.includes('survey')
    const iconType = isQuick ? 'rocket' : isBrain ? 'brain' : 'video'
    const iconName = item.isComingSoon ? `${iconType}-disabled` : iconType
    return commonUtils.getImagePath(`productSwitchModal/${iconName}.png`)
  }

  return (
    <div className={classNames(styles.productSwitchHoverPanel, className)} data-testid='product-switch-hover-panel'>
      <div className={styles.columnNav}>
        {columns.map((column, index) => {
          const active = column.key === activeColumn?.key
          return (
            <button
              key={column.key}
              type='button'
              data-testid={`product-switch-hover-tab-${column.key}`}
              className={classNames(
                styles.columnTab,
                {
                  [styles.active]: active,
                },
                {
                  [styles.tonePurple]: column.tone === 'purple',
                  [styles.toneBlue]: column.tone === 'blue',
                },
                {
                  [styles.withDivider]: index !== columns.length - 1,
                }
              )}
              onClick={() => setActiveColumnKey(column.key)}
            >
              {active && <span className={styles.activeBar} />}
              <span className={styles.columnTitle}>{column.bannerTitle}</span>
            </button>
          )
        })}
      </div>

      <div className={styles.detailArea}>
        <p className={styles.assetText}>
          {activeColumn?.assetText || ''}
        </p>
        <div className={styles.itemList}>
          {activeColumn?.items.map((item) => {
            return (
              <button
                key={item.key}
                type='button'
                data-testid={`product-switch-hover-option-${item.key}`}
                disabled={item.isComingSoon}
                className={classNames(styles.item, {
                  [styles.comingSoon]: item.isComingSoon,
                })}
                onClick={() => item.onClick?.()}
              >
                <img className={styles.itemIcon} src={getIconSrc(item)} alt='' />
                <div className={styles.itemContent}>
                  <div className={styles.itemTitleRow}>
                    <h3 className={styles.itemTitle}>{item.title}</h3>
                    {item.isComingSoon && (
                      <span className={styles.itemComingSoonTag}>
                        {t('productSwitch.common.comingSoon')}
                      </span>
                    )}
                  </div>
                  <p className={styles.itemDescription}>{item.description}</p>
                </div>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default ProductSwitchHoverPanel
